/**
 * Meet's numbers and pure rules (§5.17, decision 163), shared by the api, the worker and the web. No
 * zod here, so the web loads it cheaply. The numbers become console settings in a later pass (FR-MET-22).
 */
/** minutes an unanswered Now proposal lives, and a later one (or until its time, if sooner) */
export const MEET_PROPOSAL_NOW_MIN = 10;
export const MEET_PROPOSAL_LATER_MIN = 60;
/** a Now meet's time is this long after it's confirmed */
export const MEET_NOW_START_MIN = 20;
/** a later meet more than this far out gets Still on? this long before (FR-MET-05) */
export const MEET_RECONFIRM_OVER_MIN = 30;
export const MEET_RECONFIRM_MIN = 20;
/** On my way opens this long before the time (FR-MET-06) */
export const MEET_LEAVE_WINDOW_MIN = 60;
/** no-show timers (FR-MET-14): not set off by the time plus this; not arrived by the ETA plus this; door unanswered */
export const MEET_NOT_LEFT_MIN = 15;
export const MEET_NOT_ARRIVED_MIN = 30;
export const MEET_DOOR_MIN = 15;
/** Did they show? left unanswered this long ends the meet with nothing recorded */
export const MEET_ASK_HOURS = 24;
/** a later meet can be called off freely until this long before its time */
export const MEET_FREE_CALL_OFF_MIN = 60;
/** a host may call off freely once the traveler is this far past their first ETA */
export const MEET_LATE_GRACE_MIN = 20;
/** Shows up (FR-MET-15) */
export const MEET_RECORD_COUNT = 10;
export const MEET_RECORD_DAYS = 180;
export const MEET_NEW_UNDER = 3;
export const MEET_REPEAT_DAYS = 7;
/** warnings and pauses (FR-MET-16) */
export const MEET_WARN_AFTER = 2;
export const MEET_PAUSE_AFTER = 3;
export const MEET_WINDOW_DAYS = 30;
export const MEET_PAUSE_DAYS = 14;
export const MEET_REPAUSE_DAYS = 30;
export const MEET_REPAUSE_WITHIN_DAYS = 90;
export const MEET_LATE_WARN_AFTER = 3;
/** disputes (FR-MET-17): how long after the record, and how many in how many days */
export const MEET_DISPUTE_HOURS = 72;
export const MEET_DISPUTES_ALLOWED = 2;
export const MEET_DISPUTE_WINDOW_DAYS = 90;
/** wrong-place reports from different members that hold a host's account (FR-MET-14) */
export const MEET_WRONG_PLACE_HOLD_AFTER = 2;
export const MEET_WRONG_PLACE_WINDOW_DAYS = 90;
/** Is this them? (FR-MET-13): asked for a day after Met; two "Not really" from different members in 90 days open a case */
export const MEET_PHOTO_ASK_HOURS = 24;
export const MEET_PHOTO_CASE_AFTER = 2;
export const MEET_PHOTO_WINDOW_DAYS = 90;
/** Tell a friend (FR-MET-18): check in up to this long ahead; the page answers until this long after */
export const MEET_FRIEND_MAX_HOURS = 24;
export const MEET_FRIEND_KEEP_HOURS = 24;
/** after a meet is over it stays in the chat until you end it, for this long at most */
export const MEET_STAYS_OPEN_HOURS = 24;
/** a host with fewer than this many met hosted meets is new: Tell a friend starts on */
export const MEET_NEW_HOST_UNDER = 3;
/** the pair-level record goes this long after the meet ends (FR-MET-21) */
export const MEET_KEEP_DAYS = 30;
/** proposed or confirmed at once (FR-MET-02) */
export const MEET_MAX_OPEN = 3;
/** location (FR-MET-09, 10, 12) */
export const MEET_FIX_EVERY_S = 15;
export const MEET_SHARE_MAX_MIN = 120;
export const MEET_SHARE_AFTER_ARRIVE_MIN = 15;
export const MEET_ARRIVE_M = 75;
export const MEET_NEAR_M = 150;
export const MEET_NEAR_WITHIN_MIN = 10;
export const MEET_HOST_AWAY_M = 300;
export const MEET_HOST_ACCURACY_M = 75;
export const MEET_NUDGE_M = 50;
export const MEET_CHECK_EVERY_S = 60;
export const MEET_CHECK_STALE_MIN = 15;
/** the ciphertext the server holds for a meet lives no longer than this (seconds) */
export const MEET_BLOB_TTL_S = 6 * 60 * 60;
export const MEET_MODE_LABEL = {
    driving: 'Driving',
    walking: 'Walking',
    riding: 'Riding',
    transit: 'Transit',
};
/** km/h by mode, for the estimate worked out on the traveler's phone (FR-MET-07) */
const SPEED_KMH = { driving: 30, walking: 5, riding: 15, transit: 18 };
/** streets aren't straight lines */
const ROAD_FACTOR = 1.35;
/** metres between two points */
export function metersBetween(a, b) {
    const R = 6_371_000;
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** minutes to get there, at least 1 while still away */
export function etaMinutes(meters, mode) {
    if (meters < MEET_ARRIVE_M)
        return 0;
    return Math.max(1, Math.round(((meters / 1000) * ROAD_FACTOR * 60) / SPEED_KMH[mode]));
}
/**
 * The Shows up line (FR-MET-15) and its warning (FR-MET-16) from a member's outcomes, any order.
 * `whose` picks the words: "their" on someone else's card, "your" on your own.
 */
export function meetRecord(outcomes, now = new Date(), whose = 'their') {
    const t = (o) => new Date(o.endedAt).getTime();
    const since = (days) => now.getTime() - days * 86_400_000;
    const sorted = [...outcomes].sort((a, b) => t(b) - t(a));
    const counted = sorted
        .filter((o) => o.outcome !== 'late_call_off' && t(o) >= since(MEET_RECORD_DAYS))
        .slice(0, MEET_RECORD_COUNT);
    const showed = counted.filter((o) => o.outcome === 'met').length;
    const noShowsThisMonth = sorted.filter((o) => o.outcome === 'no_show' && t(o) >= since(MEET_WINDOW_DAYS)).length;
    const lateThisMonth = sorted.filter((o) => o.outcome === 'late_call_off' && t(o) >= since(MEET_WINDOW_DAYS)).length;
    const line = counted.length < MEET_NEW_UNDER
        ? 'New to meeting'
        : `Shows up · ${showed} of ${whose} last ${counted.length}`;
    const warn = noShowsThisMonth >= MEET_WARN_AFTER
        ? `Missed ${noShowsThisMonth} meets this month`
        : lateThisMonth >= MEET_LATE_WARN_AFTER
            ? 'Often calls off late'
            : '';
    return { line, warn, showed, counted: counted.length, noShowsThisMonth };
}
/** "10:30 PM" in the viewer's own clock */
export const meetTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
/**
 * Meet for an event (decision 193). A guest who set off and hasn't arrived is asked about (the host's
 * Did they show?) once the event has started and they're past their first estimate (or an hour after
 * setting off) by the not-there time, until a day after it ends.
 */
export function eventMeetAskable(ev, leg, now) {
    if (!leg.leftAt || leg.arrivedAt || leg.outcome)
        return false;
    const due = (leg.etaAt?.getTime() ?? leg.leftAt.getTime() + 60 * 60_000) + MEET_NOT_ARRIVED_MIN * 60_000;
    return (now >= Math.max(ev.startsAt.getTime(), due) && now <= ev.endsAt.getTime() + MEET_ASK_HOURS * 3_600_000);
}
/**
 * A guest's phone sends where they are (FR-MET-09's rules): from On my way, until they stop it, two
 * hours on, 15 minutes after they're there, or the event's end.
 */
export function eventMeetSharing(ev, leg, now) {
    if (!leg.leftAt || leg.stopped || now > ev.endsAt.getTime())
        return false;
    if (now > leg.leftAt.getTime() + MEET_SHARE_MAX_MIN * 60_000)
        return false;
    return !leg.arrivedAt || now <= leg.arrivedAt.getTime() + MEET_SHARE_AFTER_ARRIVE_MIN * 60_000;
}
//# sourceMappingURL=meet-rules.js.map